<template>
  <AppModal
    :id="props.id"
    v-model:is-open="isOpen"
    role="alertdialog"
    :aria-describedby="descriptionId"
  >
    <template #title>
      <HeadingIcon alt="" />
      {{ props.problem?.summary }}
    </template>
    <template #body>
      <div :id="descriptionId">
        <slot
          name="body"
          :close="close"
        >
          {{ props.problem?.fix }}
        </slot>
      </div>
    </template>
    <template #footer>
      <AppButton
        :button-style="ButtonStyle.OUTLINE"
        @click="isOpen = false"
      >
        Close
      </AppButton>
      <AppButton
        v-if="props.problem?.resolution"
        :aria-label="props.problem.resolution.accessibleName"
        @click="resolve"
      >
        {{ props.problem.resolution.label }}
      </AppButton>
    </template>
  </AppModal>
</template>

<!--
 Reports a failure the user cannot act on where they are. The problem's summary
 heads the dialog, its fix is the body, and its resolution is the action — see ADR 0011.
 -->
<script setup lang="ts">
import { ref, watch } from 'vue'

import HeadingIcon from '@/assets/images/error-2.svg'
import AppButton from '@/components/atomic/AppButton/AppButton.vue'
import AppModal, { type Props as AppModalProps } from '@/components/atomic/AppModal/AppModal.vue'
import { ButtonStyle } from '@/models/Button'
import type { UserProblem } from '@/types/UserProblem'

// The open state is derived from props.problem, so it is not exposed to consumers.
export interface Props extends Omit<AppModalProps, 'isOpen'> {
  problem?: UserProblem
}
const props = withDefaults(defineProps<Props>(), { problem: undefined })

const isOpen = ref<boolean>(false)
const descriptionId = `${props.id}-description`
const emit = defineEmits<{ close: []; resolve: [problem: UserProblem] }>()

watch(
  () => props.problem,
  (problem) => {
    isOpen.value = problem !== undefined
  },
  { immediate: true }
)

// However it is closed — the button, Escape or the backdrop — the parent hears once.
watch(isOpen, (open: boolean) => {
  if (!open) {
    emit('close')
  }
})

// Lets body content that takes the user elsewhere close the dialog on its way.
function close(): void {
  isOpen.value = false
}

// Resolving is answered before the modal closes, so the parent acts and then clears.
function resolve(): void {
  if (props.problem) {
    emit('resolve', props.problem)
  }
  isOpen.value = false
}
</script>

<style lang="scss" scoped>
:deep(.modal-title) {
  color: var(--hearsee-background-bg-warning);
}
</style>
